import type { Components } from '@mui/material/styles/components';
import type { Theme } from '@mui/material/styles/createTheme';

export const MuiButton: Components<Theme>['MuiButton'] = {
  styleOverrides: {
    loadingIndicator: ({ theme }) => ({
      color: theme.palette.primary.dark,
    }),
  },
  variants: [
    {
      props: { variant: 'contained', color: 'primary' },
      style: {
        '&.Mui-disabled .MuiButton-loadingIndicator': {
          color: '#ffffff',
        },
        '&.Mui-disabled': {
          color: '#ffffff',
        },
      },
    },
    {
      props: { variant: 'contained', color: 'warning' },
      style: {
        backgroundColor: '#FAF0E7',
        color: '#CF6300',
      },
    },
    {
      props: { variant: 'contained', color: 'secondary' },
      style: {
        backgroundColor: '#EEF2FD',
        color: '#6948EB',
        '&:hover': {
          backgroundColor: '#D4DFF9',
        },
        '&.Mui-disabled': {
          backgroundColor: '#eceff6',
        },
      },
    },
    {
      props: {
        variant: 'contained',
        size: 'large',
      },
      style: {
        minHeight: '60px',
        fontSize: '16px',
        lineHeight: 1.5,
        fontWeight: 600,
        borderRadius: '10px', // TODO: move under ButtonBase?
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none !important',
        },
        padding: '18px 22px',
        textTransform: 'none',
      },
    },
    {
      props: {
        variant: 'contained',
        size: 'medium',
      },
      style: {
        height: '45px',
        fontSize: '1rem',
        lineHeight: '1.1875rem',
        fontWeight: 700,
        borderRadius: '10px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none !important',
        },
        padding: '10px',
        textTransform: 'none',
      },
    },
    {
      props: {
        variant: 'contained',
        size: 'small',
      },
      style: {
        minHeight: '30px',
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: 600,
        borderRadius: '10px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none !important',
        },
        padding: '4px 10px',
        textTransform: 'none',
      },
    },
    {
      props: { variant: 'outlined', color: 'primary' },
      style: {
        '&.Mui-disabled': {
          backgroundColor: '#D4DFF9',
          color: '#FFFFFF',
          '& .MuiButton-icon svg path': {
            fill: '#FFFFFF',
          },
        },
      },
    },
    {
      props: {
        variant: 'outlined',
        size: 'large',
      },
      style: {
        height: '60px',
        fontSize: '1rem',
        lineHeight: '1.1875rem',
        fontWeight: 700,
        borderRadius: '10px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none !important',
        },
        padding: '16.5px 22px',
        textTransform: 'none',
      },
    },
    {
      props: {
        variant: 'outlined',
        size: 'medium',
      },
      style: {
        height: '45px',
        fontSize: '1rem',
        lineHeight: '1.1875rem',
        fontWeight: 700,
        borderRadius: '10px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none !important',
        },
        padding: '10px',
        textTransform: 'none',
      },
    },
    {
      props: {
        variant: 'outlined',
        size: 'medium',
        disabled: true,
      },
      style: {
        color: '#A9A9A9',
      },
    },
    {
      props: {
        variant: 'outlined',
        size: 'small',
      },
      style: ({ theme }) => ({
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: 600,
        borderRadius: '10px',
        borderColor: theme.palette.primary.main,
        backgroundColor: 'transparent',
        color: theme.palette.primary.main,
        boxShadow: 'none',
        padding: '4px 10px',
        textTransform: 'none',
      }),
    },
    {
      props: {
        variant: 'text',
        size: 'small',
      },
      style: {
        fontSize: '14px',
        lineHeight: 1.5,
        boxShadow: 'none',
        textTransform: 'none',
        textDecoration: 'none',
        textUnderlineOffset: '2px',
        fontWeight: 600,
        '&:hover': {
          boxShadow: 'none !important',
          backgroundColor: '#FEFEFE',
          borderColor: '#e9e9e9',
        },
      },
    },
    {
      props: {
        variant: 'text',
        disabled: true,
        size: 'medium',
      },
      style: {
        color: '#A9A9A9 !important',
        backgroundColor: 'unset',
      },
    },
    {
      props: {
        variant: 'text',
        size: 'medium',
      },
      style: {
        textTransform: 'none',
        height: '45px',
        color: '#0C122A',
        fontSize: '1rem',
        lineHeight: '1.5',
        fontWeight: 600,
        padding: '13px 22px',
        '&:hover': {
          boxShadow: 'none !important',
          backgroundColor: '#FEFEFE',
        },
        '.MuiButton-endIcon': {
          marginLeft: '4px',
        },
      },
    },
    {
      props: {
        variant: 'text',
        size: 'medium',
        color: 'primary',
      },
      style: {
        color: '#6948EB',
        '&:hover': {
          color: '#5E3CE1',
        },
      },
    },
    {
      props: {
        variant: 'text',
        size: 'large',
      },
      style: {
        textTransform: 'none',
        color: '#6948EB',
        fontSize: '16px',
        lineHeight: 1.5,
        fontWeight: 600,
        padding: '18px 0',
        '&:hover': {
          backgroundColor: '#fff',
        },
        '.MuiButton-endIcon': {
          marginLeft: '4px',
        },
      },
    },
    {
      props: {
        variant: 'contained',
        color: 'success',
      },
      style: {
        backgroundColor: '#DFF1E5',
        color: '#3CA73B',
        border: '1px solid #DFF1E5',
        '&:hover': {
          backgroundColor: '#AFDDBF',
          color: '#059436',
        },
      },
    },
    {
      props: { variant: 'text', color: 'error' },
      style: {
        color: '#E02727',
      },
    },
    {
      props: { variant: 'outlined', color: 'error' },
      style: {
        borderColor: '#E02727',
        color: '#E02727',
      },
    },
    {
      props: { variant: 'outlined', color: 'neutral' as any },
      style: {
        borderColor: '#E6E6E6',
        backgroundColor: 'transparent',
        '&:hover': {
          borderColor: '#b5b5b5',
        },
      },
    },
    {
      props: {
        variant: 'outlined',
        color: 'info',
        size: 'large',
      },
      style: {
        padding: '18px 16px',
        backgroundColor: '#FFFFFF',
        color: '#071B3ACC',
        border: '1px solid #D4D8E3',
        fontSize: '14px',
        fontWeight: 500,
        justifyContent: 'flex-start',
        gap: '12px',
        textAlign: 'left',
        '& .prefix-icon': {
          width: '20px',
          height: '18px',
          path: {
            fill: '#071B3ACC',
          },
        },
        '& .suffix-icon': {
          marginLeft: 'auto',
          marginRight: '8px',
        },
      },
    },
  ],
};
